/**
 * Site Feedback Modal Component
 * 
 * Replicates the feedback functionality from master.html template
 * with modal dialog for submitting feedback with optional screenshot.
 * 
 * Issue #74 - Master Layout Migration
 */

import { useState } from 'react';
import { tecDocToast } from '../../utils/toast';
import { useAPIService } from '../../services/apiService';
import './FeedbackModal.css';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  feedbackEmail: string;
  globalContext: {
    authContext?: {
      csrf_token?: string;
    };
  };
}

function FeedbackModal({ isOpen, onClose, feedbackEmail }: FeedbackModalProps) {
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const apiService = useAPIService();

  const handleSubmit = async () => {
    if (!feedback.trim()) {
      tecDocToast.error('Please enter your feedback before sending.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Attempt to capture screenshot like original implementation
      let screenshotData = '';
      let fileName = '';
      
      if ((window as any).html2canvas) {
        try {
          const canvas = await (window as any).html2canvas(document.body);
          screenshotData = canvas.toDataURL('image/png');
          fileName = `feedback-${Date.now()}.png`;
        } catch (error) {
          console.warn('Screenshot capture failed:', error);
          // Continue without screenshot
        }
      }

      // Send feedback using the centralized API service
      await apiService.post('/send-feedback', {
        to_email: feedbackEmail,
        feedback: feedback,
        file_name: fileName,
        screenshot: screenshotData
      }, 'Sending feedback...');

      // Show success message using react-hot-toast
      setFeedback('');
      onClose();
      
      // Close modal first, then show toast with a small delay
      setTimeout(() => {
        tecDocToast.success('Email Sent');
      }, 100);
      
    } catch (error) {
      console.error('Feedback submission error:', error);
      tecDocToast.error('Failed to send feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="feedback-modal-overlay" onClick={onClose}>
      <div className="feedback-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="feedback-modal-header">
          <h2>Site Feedback</h2>
          <button className="feedback-modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="feedback-container">
          <div className="feedback-form-content">
            <h3 className="feedback-header">Your Feedback</h3>
            <textarea
              className="feedback-textarea"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={12}
              placeholder="Please describe your feedback, suggestions, or issues..."
              disabled={isSubmitting}
            />
            <div className="feedback-actions">
              <button 
                className="tec-button-md tec-button-red"
                onClick={handleSubmit}
                disabled={isSubmitting || !feedback.trim()}
              >
                {isSubmitting ? 'Sending...' : 'Send Feedback'}
              </button>
              <button 
                className="tec-button-md tec-button-gray"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FeedbackModal;